import { KitListItem, KitText } from '@omni/kit/components';
import { EventImage } from '@omni/kit/components/EventImage';
import { SizeClassV2, useSizeClass } from '@omni/kit/contexts/SizeClassContext';
import { useTranslation } from '@omni/kit/hooks/useTranslation';
import Colors from '@omni/kit/theming/Colors';
import Spacing from '@omni/kit/theming/Spacing';
import {
  NavigationProp,
  ParamListBase,
  useNavigation,
} from '@react-navigation/native';
import { GroupChannel } from '@sendbird/chat/groupChannel';
import React, { useContext } from 'react';
import { Platform, SectionList, View } from 'react-native';
import { SafeAreaInsetsContext } from 'react-native-safe-area-context';
import { useSelector } from 'react-redux';

import { IGroupEventAllListProps } from '../../../../Types';
import { GroupedEventSection } from '../../../../hooks/useTransformGroupEvents';
import * as Constants from '../../../Constants';
import { channelSelector } from '../../../redux/selectors';
import GroupEventsEmptyState from './GroupEventsEmptyState';

export default function GroupAllListEvents({
  sections,
  onRefresh,
  isRefreshing,
  accessToken,
  onPressCreateGroupEvent,
}: IGroupEventAllListProps): JSX.Element {
  const insets = useContext(SafeAreaInsetsContext);
  const { t } = useTranslation();
  const channel = useSelector(channelSelector) as GroupChannel;
  const isManager = channel?.myRole === 'operator';
  const { windowHeight, windowWidth, sizeClassV2 } = useSizeClass();
  const isLandscape = windowHeight < windowWidth;
  const isLarge = sizeClassV2 >= SizeClassV2.L;
  // Navigation
  let navigation: NavigationProp<ParamListBase>;
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    navigation = useNavigation();
  } catch {
    navigation = {} as NavigationProp<ParamListBase>; // Provide a fallback object
  }

  return (
    <View
      style={[
        isLarge && {
          marginHorizontal: Spacing.xxxxl - 10,
          marginTop: 6,
        },
      ]}
    >
      <SectionList
        renderItem={({ item }) => (
          <KitListItem
            ImageComponent={
              <EventImage url={item?.image} date={item?.start_at} />
            }
            title={item.title}
            subtitleLength={2}
            secondarySubtitle={item.eventSubtitle}
            rightActionIcon='action-h'
            showRightIconOnAndroid={true}
            minHeight={78}
            style={{
              paddingHorizontal: Spacing.l,
            }}
            onPress={() =>
              navigation.navigate(Constants.EVENT_DETAILS_SCREEN, {
                eventId: item.id,
              })
            }
          />
        )}
        sections={sections}
        keyExtractor={(item, index) => item.title + index}
        ListEmptyComponent={
          <GroupEventsEmptyState
            isManager={isManager}
            onPressCreateGroupEvent={onPressCreateGroupEvent}
          />
        }
        ListFooterComponent={
          <View
            style={{
              width: '100%',
              backgroundColor: Colors.N0,
              alignItems: 'center',
              justifyContent: 'center',
              paddingVertical: Spacing.xl,
              paddingBottom: Platform.OS === 'web' ? 24 : insets?.bottom,
            }}
          >
            {sections.length ? (
              <KitText fontSize={14} color={Colors.N500} lineHeight={20}>
                {t('groups:maximumUpcomingEvents')}
              </KitText>
            ) : null}
          </View>
        }
        onRefresh={onRefresh}
        refreshing={isRefreshing}
        renderSectionHeader={({ section }) => {
          // Get the index of the section
          const sectionIndex = sections?.findIndex(
            (sec: GroupedEventSection) =>
              sec.sectionHeader === section.sectionHeader
          );

          return (
            <View
              style={{
                marginTop: sectionIndex === 0 ? Spacing.m : Spacing.xxl, // No marginTop for the first section
                paddingHorizontal: Spacing.l,
              }}
            >
              <KitText bold fontSize={20} color={Colors.N900}>
                {section.sectionHeader}
              </KitText>
            </View>
          );
        }}
        showsVerticalScrollIndicator={false}
        stickySectionHeadersEnabled={false}
      />
    </View>
  );
}
